'use client';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useMutation } from '@parkchirp/api-client';
import { InverseButton } from '@parkchirp/ui-library';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { UPDATE_PAYOR } from '../../mutations';
import { ADD_SUBSCRIPTION_DOCUMENTS, DELETE_FILE, UPDATE_SUBSCRIPTION } from '../../queries';
import { SubscriptionTaxExemptStatus } from '../../types';
import { dayjs, FileUploadTableFile, isOwnerPortal, isTenantPortal, isValidEmail } from '../../utils';
import { ConfirmationModal } from '../modals';
import { EditSubscriptionInputs } from './editSubscriptionInputs';
import { TaxExemptHistoryTable } from './taxExemptHistoryTable';

interface ReportDoc extends FileUploadTableFile {
	fileId?: number;
}

interface Facility {
	id: string;
	facilityName: string;
	facilityNumber: string;
	organizationId: string;
	wordpressPostId: number;
	theme: any;
	timezone: string;
}

interface SubscriptionInfoPageProps {
	subscriptionId: string;
	facilityId: string;
	setShowSnackbar: any;
	setShowDialog: any;
	theme: any;
	tenantSubscriptionId?: string;
	facilityData: any;
	data: any;
	error: any;
	initLoading: any;
	refetch: any;
}

const BUCKET = 'subscription-files';
const BUCKET_DIR = 'subscription-supporting-docs';

const isValidZipCode = (zip: string) => /^\d{5}(-\d{4})?$/.test(zip);
export const SubscriptionGeneralPage = ({
	subscriptionId,
	facilityId,
	setShowSnackbar,
	setShowDialog,
	tenantSubscriptionId,
	theme,
	data,
	facilityData,
	error,
	initLoading,
	refetch,
}: SubscriptionInfoPageProps) => {
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string | null>();
	const [subscriptionName, setSubscriptionName] = useState<string | null>();
	const [payorFirstName, setPayorFirstName] = useState<string | null>();
	const [payorLastName, setPayorLastName] = useState<string | null>();
	const [accountId, setAccountId] = useState<string | null>();
	const [taxExempt, setTaxExempt] = useState<SubscriptionTaxExemptStatus>('No');
	const [manualTaxExemptions, setManualTaxExemptions] = useState({ tax1: true, tax2: true, tax3: true, tax4: true });
	const [payorType, setPayorType] = useState<string | null>();
	const [residentNonResident, setResidentNonResident] = useState<string | null>();
	const [residentUnit, setResidentUnit] = useState<string | null>();
	const [tenantNonTenant, setTenantNonTenant] = useState<string | null>();
	const [tenantName, setTenantName] = useState<string | null>();
	const [payorReadableId, setPayorReadableId] = useState<string | null>();
	const [status, setStatus] = useState<string | null>();
	const [billingProfile, setBillingProfile] = useState<string | null>();
	const [payorBillingPhone, setPayorBillingPhone] = useState<string | null>();
	const [payorBillingPhoneCode, setPayorBillingPhoneCode] = useState<number>(1);
	const [payorBillingPhoneIsValid, setPayorBillingPhoneIsValid] = useState<boolean>(true);
	const [payorBillingWorkPhone, setPayorBillingWorkPhone] = useState<string | null>();
	const [payorBillingWorkPhoneCode, setPayorBillingWorkPhoneCode] = useState<number>(1);
	const [payorBillingWorkPhoneIsValid, setPayorBillingWorkPhoneIsValid] = useState<boolean>(true);
	const [payorBillingEmail, setPayorBillingEmail] = useState<string | null>();
	const [subscriptionNotes, setSubscriptionNotes] = useState<string | null>();
	const [subscriptionInvoiceNotes, setSubscriptionInvoiceNotes] = useState<string | null>();
	const [infoAttention, setInfoAttention] = useState<string | null>();
	const [mailInvoices, setMailInvoices] = useState<string | null>();
	const [emailInvoices, setEmailInvoices] = useState<string | null>();

	const [payorCity, setPayorCity] = useState<string | null>();
	const [payorState, setPayorState] = useState<string | null>();
	const [payorStreet, setPayorStreet] = useState<string | null>();
	const [payorZip, setPayorZip] = useState<string | null>();

	const [billingCompanyName, setBillingCompanyName] = useState<string | null>();
	const [billingAddressStreet2, setBillingAddressStreet2] = useState<string | null>();
	const [billingCity, setBillingCity] = useState<string | null>();
	const [billingState, setBillingState] = useState<string | null>();
	const [billingStreet, setBillingStreet] = useState<string | null>();
	const [billingZip, setBillingZip] = useState<string | null>();

	const [emails, setEmails] = useState<any>([]);
	const [loading, setLoading] = useState<boolean>(initLoading);

	const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION);
	const [updatePayor] = useMutation(UPDATE_PAYOR);

	const [files, setFiles] = useState<ReportDoc[]>([]);

	const [facility, setFacility] = useState<Facility | null>(null);

	const [deleteFile] = useMutation(DELETE_FILE);
	const [addSubscriptionDocuments] = useMutation(ADD_SUBSCRIPTION_DOCUMENTS);

	useEffect(() => {
		if (facilityData) {
			setFacility(facilityData.getFacility);
		}
	}, [facilityData]);

	const resetInputsToApiCall = useCallback(() => {
		if (data?.getSubscriptionById) {
			const { tax1, tax2, tax3, tax4 } = data.getSubscriptionById.manualTaxExemptions;
			setSubscriptionName(data.getSubscriptionById.subscriptionName);
			setPayorFirstName(data.getSubscriptionById.payor?.billingFirstName ?? '');
			setPayorLastName(data.getSubscriptionById.payor?.billingLastName ?? '');
			setAccountId(`AC-${data.getSubscriptionById.accountId}`);
			setTaxExempt(data.getSubscriptionById.taxExempt);
			setManualTaxExemptions({ tax1, tax2, tax3, tax4 });
			setPayorType(data.getSubscriptionById.payor?.type === 'BUSINESS' ? 'Business' : 'Individual');
			setResidentNonResident(data.getSubscriptionById?.residentNonResident ?? null);
			setResidentUnit(data.getSubscriptionById?.residentUnit ?? null);
			setTenantNonTenant(data.getSubscriptionById?.tenantNonTenant ?? null);
			setTenantName(data.getSubscriptionById?.tenantName ?? null);
			setPayorReadableId(data.getSubscriptionById.payor?.readableId);
			setStatus(data.getSubscriptionById.status);
			setBillingProfile(data.getSubscriptionById.billingProfile?.name);
			setPayorBillingPhone(data.getSubscriptionById.payor?.billingPhone);
			setPayorBillingPhoneCode(data.getSubscriptionById.payor?.billingCountryCode || 1);
			setPayorBillingWorkPhone(data.getSubscriptionById.payor?.billingWorkPhone);
			setPayorBillingWorkPhoneCode(data.getSubscriptionById.payor?.billingWorkCountryCode || 1);
			setPayorBillingEmail(data.getSubscriptionById.payor?.billingEmail || 'No email was provided.');
			setSubscriptionNotes(data.getSubscriptionById.payor?.contactNotes);
			setSubscriptionInvoiceNotes(data.getSubscriptionById.subscriptionInvoiceNotes);
			setMailInvoices(data.getSubscriptionById.mailInvoices ? 'Yes' : 'No');
			setEmailInvoices(data.getSubscriptionById.emailInvoices ? 'Yes' : 'No');

			setPayorCity(data.getSubscriptionById.payor?.infoCity || '');
			setPayorState(data.getSubscriptionById.payor?.infoState || '');
			setPayorStreet(data.getSubscriptionById.payor?.infoStreet || '');
			setPayorZip(data.getSubscriptionById.payor?.infoZip || '');
			setInfoAttention(data.getSubscriptionById.payor?.infoAttn || '');

			setBillingCompanyName(data.getSubscriptionById.payor?.infoName || '');
			setBillingAddressStreet2(data.getSubscriptionById.payor?.billingAddressStreet2 || '');
			setBillingCity(data.getSubscriptionById.payor?.billingCity || '');
			setBillingState(data.getSubscriptionById.payor?.billingState || '');
			setBillingStreet(data.getSubscriptionById.payor?.billingStreet || '');
			setBillingZip(data.getSubscriptionById.payor?.billingZip || '');
			setEmails(data.getSubscriptionById.payor?.billingEmails || []);
			setFiles(data.getSubscriptionById.subscriptionSupportingDocuments || []);
		}
		setShowDialog(null);
		setHasUnsavedChanges(false);
		setIsEditMode(false);
	}, [data?.getSubscriptionById, setShowDialog]);

	useEffect(() => {
		if (data?.getSubscriptionById) {
			resetInputsToApiCall();
			setLoading(false);
		}
	}, [data, resetInputsToApiCall]);

	useEffect(() => {
		if (error?.message) {
			setErrorMessage(`Unable to fetch subscription: ${error.message}`);
		}
	}, [error]);

	useEffect(() => {
		// the handler for actually showing the prompt
		// https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
		const handler = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = '';
		};

		if (hasUnsavedChanges) {
			window.addEventListener('beforeunload', handler);
			// clean it up, if the dirty state changes
			return () => {
				window.removeEventListener('beforeunload', handler);
			};
		}
		window.removeEventListener('beforeunload', handler);
		// since this is not dirty, don't do anything
		return () => {};
	}, [hasUnsavedChanges]);

	const bucketDir = useMemo(
		() => `${BUCKET_DIR}/${facility?.organizationId}/${facility?.id}/${subscriptionName}`,
		[facility?.organizationId, facility?.id, subscriptionName],
	);

	if (loading) {
		return <LinearProgress />;
	}
	if (!initLoading && !data?.getSubscriptionById) {
		return (
			<Grid>
				<Card>
					<CardContent>
						<Alert severity='info' sx={{ mb: 1 }}>
							Subscription not found.
						</Alert>
					</CardContent>
				</Card>
			</Grid>
		);
	}

	const subscriptionHasAllRequiredFields = () => {
		const requiredFields = [
			{ label: 'Subscription Name', value: subscriptionName },
			{ label: 'Account ID', value: accountId },
			{ label: 'Tax Exempt', value: taxExempt },
			{ label: 'Subscriber Type', value: payorType },
			{ label: 'Mail Invoices', value: mailInvoices },
			{ label: 'Email Invoices', value: emailInvoices },
			{ label: 'Payor First Name', value: payorFirstName },
			{ label: 'Payor Last Name', value: payorLastName },
		];
		const missingFields = requiredFields.filter(({ value }) => ['', null, undefined].includes(value));
		if (missingFields.length) {
			const missingAsString = missingFields.map(({ label }) => label).join(', ');
			setShowSnackbar({
				message: `Unable to save Subscription due to missing field(s): ${missingAsString}`,
				severity: 'error',
			});
			return false;
		}
		const additionalValidations = [
			{
				message: 'Primary Contact Zipcode is not valid',
				failingCondition: payorZip?.length && !isValidZipCode(payorZip),
			},
			{
				message: 'Billing Zipcode is not valid',
				failingCondition: billingZip?.length && !isValidZipCode(billingZip),
			},
			{
				message: 'Phone number is not valid',
				failingCondition: !payorBillingPhoneIsValid,
			},
			{
				message: 'Work phone number is not valid',
				failingCondition: !payorBillingWorkPhoneIsValid,
			},
			{
				message: `Primary Contact Payor Address required when Mail Invoices is Yes`,
				failingCondition:
					mailInvoices === 'Yes' &&
					[payorCity, payorState, payorStreet, payorZip].filter((v) => v === '').length > 0,
			},
		];
		const failingValidations = additionalValidations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
			});
			return false;
		}
		return true;
	};

	const toggleEditMode = async () => {
		if (isEditMode) {
			const areEmailsValid = emails.every((email) => isValidEmail(email.email));
			if (!areEmailsValid) {
				setShowSnackbar({
					message: 'Invalid invoice email recipient. Please correct before saving subscription',
					severity: 'error',
					onClose: () => setShowSnackbar(null),
				});
				return;
			}
			setEmails(emails.filter(({ isNew }) => typeof isNew === 'undefined')); // remove unsaved new emails
			if (!subscriptionHasAllRequiredFields()) {
				return;
			}
			try {
				setLoading(true);
				await updateSubscription({
					variables: {
						facilityId,
						isFromTenantPortal: isTenantPortal,
						subscription: {
							subscriptionName: subscriptionName || data.getSubscriptionById.subscriptionName,
							subscriptionInvoiceNotes,
							taxExempt,
							manualTaxExemptions,
							mailInvoices: mailInvoices === 'Yes',
							emailInvoices: emailInvoices === 'Yes',
							residentNonResident,
							residentUnit,
							tenantNonTenant,
							tenantName,
						},
						subscriptionId: data.getSubscriptionById.id,
					},
					context: {
						apiVersion: 'v2',
					},
				});
				const originalBillingEmailIds = emails.map(({ id }) => id);
				// make a copy
				const billingEmailsWithDeleteFlag = emails.map((email) => {
					const { id, ...rest } = email;
					if (typeof id === 'number') {
						return rest;
					}
					return email;
				});
				if (data.getSubscriptionById.payor.billingEmails.length) {
					data.getSubscriptionById.payor.billingEmails.forEach((email) => {
						if (originalBillingEmailIds.indexOf(email.id) === -1) {
							billingEmailsWithDeleteFlag.push({ action: 'DELETE', ...email });
						}
					});
				}
				await updatePayor({
					variables: {
						tenantSubscriptionId,
						payor: {
							id: data.getSubscriptionById.payor.id,
							type: payorType,
							contactNotes: subscriptionNotes,
							billingFirstName: payorFirstName.trim(),
							billingLastName: payorLastName.trim(),
							billingCompanyName: billingCompanyName.trim(),
							billingStreet: billingStreet.trim(),
							billingAddressStreet2: billingAddressStreet2.trim(),
							billingCity: billingCity.trim(),
							billingState: billingState.trim(),
							billingZip: billingZip.trim(),
							billingPhone: payorBillingPhone,
							billingCountryCode: payorBillingPhoneCode,
							billingWorkPhone: payorBillingWorkPhone,
							billingWorkCountryCode: payorBillingWorkPhoneCode,
							billingEmails: billingEmailsWithDeleteFlag.map(({ id, email, action }) => ({
								id,
								email,
								action,
							})),
							infoStreet: payorStreet.trim(),
							infoCity: payorCity.trim(),
							infoState: payorState.trim(),
							infoZip: payorZip.trim(),
							billingAttn: infoAttention.trim(),
						},
					},
					context: {
						apiVersion: 'v2',
					},
				});
				setIsEditMode(!isEditMode);
				refetch();
				setShowSnackbar({
					message: 'Subscription saved successfully.',
					severity: 'success',
				});
			} catch (e) {
				setShowSnackbar({
					message: `Unable to save subscription: ${e}`,
					severity: 'error',
				});
			} finally {
				setLoading(false);
			}
		} else {
			setIsEditMode(!isEditMode);
		}
	};

	const handleCancel = () => {
		if (hasUnsavedChanges) {
			const confirmationModal = (
				<ConfirmationModal
					title='Are you sure you want to cancel editing?'
					content={`There are unsaved changes for ${subscriptionName}`}
					dismissText='Cancel'
					type='text'
					confirmText='Confirm'
					handleDismiss={() => setShowDialog(null)}
					handleConfirm={() => resetInputsToApiCall()}
				/>
			);
			setShowDialog(confirmationModal);
		} else {
			resetInputsToApiCall();
		}
	};

	const renderSaveOrEditButtons = () => {
		if (isEditMode) {
			return (
				<>
					<InverseButton
						fullWidth={false}
						onClick={handleCancel}
						data-cy='subscription-cancel-button'
						name='Cancel'
						theme={theme}
					/>
					<Button onClick={toggleEditMode} data-cy='subscription-save-button'>
						Save
					</Button>
				</>
			);
		}
		if (isOwnerPortal) {
			return null;
		}

		return (
			<Button onClick={toggleEditMode} data-cy='subscription-edit-button'>
				Edit
			</Button>
		);
	};

	const onAddFiles = async (incomingFiles: FileUploadTableFile[]) => {
		const newFiles = incomingFiles.filter((file) => !files.find((f) => f.fileName === file.fileName));

		await addSubscriptionDocuments({
			variables: {
				facilityId,
				organizationId: facility?.organizationId,
				subscriptionId,
				files: newFiles.map((file) => ({
					type: 'SUBSCRIPTION_DOC',
					fileName: file.fileName,
					bucket: BUCKET,
					bucketDir,
				})),
			},
			context: {
				apiVersion: 'v2',
			},
			onCompleted: ({ addSubscriptionDocuments: addSubscriptionDocumentsResponse }) => {
				setFiles([...files, ...addSubscriptionDocumentsResponse]);
			},
			onError: (err) => {
				setShowSnackbar({
					message: err.message,
					autoHideDuration: 3000,
					severity: 'error',
				});
			},
		});
	};

	const onDeleteFile = async (file) => {
		if (file?.id) {
			try {
				await deleteFile({
					variables: {
						fileId: file.id,
					},
					context: {
						apiVersion: 'v2',
					},
				});
				const updatedFiles = files.filter(({ id }) => id !== file.id);
				setFiles(updatedFiles);
			} catch (err) {
				setShowSnackbar({
					message: `Unable to delete file: ${err.message}`,
					severity: 'error',
				});
			}
		}
	};

	return (
		<>
			{errorMessage && (
				<Grid margin='30px'>
					<Card>
						<CardContent>
							<Alert severity='error' sx={{ mb: 1 }}>
								{errorMessage}
							</Alert>
						</CardContent>
					</Card>
				</Grid>
			)}
			{!tenantSubscriptionId && (
				<Typography variant='h4' component='h4' style={{ marginTop: '25px' }}>
					{renderSaveOrEditButtons()}
				</Typography>
			)}
			{tenantSubscriptionId && (
				<Grid
					container
					direction='row'
					justifyContent='right'
					alignItems='center'
					style={{ marginTop: '15px' }}
				>
					{renderSaveOrEditButtons()}
				</Grid>
			)}
			<EditSubscriptionInputs
				{...{
					refetch,
					isEditMode,
					setHasUnsavedChanges,
					subscriptionName,
					payorFirstName,
					payorLastName,
					payorId: data?.getSubscriptionById?.payor?.id,
					payorCognitoSub: data?.getSubscriptionById?.payor?.cognitoSub,
					payorWordpressUserId: data?.getSubscriptionById?.payor?.wordpressUserId,
					accountId,
					taxExempt,
					manualTaxExemptions,
					payorType,
					payorReadableId,
					status,
					billingProfile,
					payorBillingPhone,
					payorBillingPhoneCode,
					payorBillingWorkPhone,
					payorBillingWorkPhoneCode,
					payorBillingEmail,
					subscriptionNotes,
					subscriptionInvoiceNotes,
					infoAttention,
					payorCity,
					payorState,
					payorStreet,
					payorZip,
					billingCompanyName,
					billingAddressStreet2,
					billingCity,
					billingState,
					billingStreet,
					billingZip,
					emails,
					mailInvoices,
					emailInvoices,
					files,
					bucket: BUCKET,
					bucketDir,
					onAddFiles,
					onDeleteFile,
					setSubscriptionName,
					setPayorFirstName,
					setPayorLastName,
					setTaxExempt,
					setManualTaxExemptions,
					setPayorType,
					setPayorBillingPhone,
					setPayorBillingPhoneCode,
					setPayorBillingPhoneIsValid,
					setPayorBillingWorkPhone,
					setPayorBillingWorkPhoneCode,
					setPayorBillingWorkPhoneIsValid,
					setPayorBillingEmail,
					setSubscriptionNotes,
					setSubscriptionInvoiceNotes,
					setInfoAttention,
					setPayorCity,
					setPayorState,
					setPayorStreet,
					setPayorZip,
					setBillingCompanyName,
					setBillingAddressStreet2,
					setBillingCity,
					setBillingState,
					setBillingStreet,
					setBillingZip,
					setEmails,
					setMailInvoices,
					setEmailInvoices,
					setShowDialog,
					setShowSnackbar,
					tenantSubscriptionId,
					residentNonResident,
					setResidentNonResident,
					residentUnit,
					setResidentUnit,
					tenantNonTenant,
					setTenantNonTenant,
					tenantName,
					setTenantName,
				}}
			/>
			{data?.getSubscriptionById?.subscriptionTaxExemptTaxes?.length > 0 && (
				<TaxExemptHistoryTable
					data={data.getSubscriptionById?.subscriptionTaxExemptTaxes}
					taxTableTaxes={data.getSubscriptionById?.billingProfile?.facility?.taxTableCity?.taxTableTaxes}
					accountId={data.getSubscriptionById?.accountId}
					subscriptionId={subscriptionId}
					facilityName={facilityData?.getFacility?.facilityName}
					facilityNumber={facilityData?.getFacility?.facilityNumber}
					timezone={facilityData?.getFacility?.timezone}
					organizationId={facilityData?.getFacility?.organizationId}
					subscriptionName={data.getSubscriptionById?.subscriptionName}
					facilityId={facilityId}
					taxExempt={taxExempt}
					payorId={data.getSubscriptionById.payor?.id}
					status={data.getSubscriptionById.status}
					refetch={refetch}
				/>
			)}
		</>
	);
};
